import { Howl, Howler } from 'howler';
import { IAudio } from './IAudio';
import type { AssetManager } from '../Assets';
import { GlobalBus } from '@core/Bus/EventBus';
import {
  sanitizeAudioStorage,
  AUDIO_STORAGE_KEYS,
  DEFAULT_MUSIC_MUTED,
  DEFAULT_SOUND_MUTED,
} from './audioStorage';
import { registerActiveAudio, unregisterActiveAudio } from './audioBus';

// Howler's default HTML5-audio pool (10) is filled once, lazily, on the
// first touch/click unlock gesture — must be raised before that fires.
Howler.html5PoolSize = 25;

export type HowlerBackendOptions = {
  slug: string;
  assetManager: AssetManager;
  soundMap?: Record<string, string>;
  /** Per-sound Howler volume overrides (game-specific, e.g. sky-x new). */
  soundVolumes?: Record<string, number>;
  duckMusicOnSfx?: boolean;
  musicDuckRatio?: number;
};

export class HowlerBackend implements IAudio {
  private slug: string;
  private assets: AssetManager;
  private volume = 0.35; // Increased global volume to 1.0 for full control via individual volumes
  private sfxMuted = DEFAULT_SOUND_MUTED;
  private musicMuted = DEFAULT_MUSIC_MUTED;
  private tabHidden = false; // Track if tab is hidden
  /** False until a real click/tap/key so we never resume AudioContext on load. */
  private userHasInteracted = false;

  // LocalStorage keys for persistence
  private static readonly STORAGE_KEY_SFX_MUTED = AUDIO_STORAGE_KEYS.SFX;
  private static readonly STORAGE_KEY_MUSIC_MUTED = AUDIO_STORAGE_KEYS.MUSIC;
  private sounds: Map<string, Howl> = new Map();
  /** Optional per-sound volume map from game config (overrides built-in defaults). */
  private soundVolumes?: Record<string, number>;
  /** Active loop SFX instances (name → sound id). */
  private loopSounds: Map<string, number> = new Map();
  private currentMusic: { howl: Howl; id?: number } | null = null;
  private currentMusicName: string | null = null;
  // True from music.play() until its native play() promise settles
  // ('play' or 'playerror'). Howler's Howl.stop() silently queues instead of
  // applying while a play() promise is pending (_playLock), so restarting
  // music in that window makes Howler create a brand-new Sound instead of
  // reusing the old one — each occurrence permanently burns one more slot
  // from the global HTML5 audio pool (pool: 1 for music). Guard against
  // triggering a restart while one is already in flight.
  private musicPlayPending = false;
  private musicHealthCheckInterval: ReturnType<typeof setInterval> | null =
    null;
  private musicStartTime: number = 0;
  private musicDuration: number = 0;
  private lastLoopTime: number = 0;
  private duckMusicOnSfx: boolean;
  private musicDuckRatio: number;
  private activeSfxCount = 0;
  private musicNormalVolume = 0.2;
  private soundsReady: Promise<void>;
  private pendingMusicRequest: string | null = null;
  private musicAutoplayListener: (() => void) | null = null;
  /** Bumped when SFX are stopped for mute so stale playWithCallback onEnd handlers no-op. */
  private sfxCallbackEpoch = 0;
  private readonly onVisibilityChange = () => {
    if (document.hidden) {
      console.log('[HowlerBackend] Tab hidden, muting all audio...');
      this.tabHidden = true;
      Howler.mute(true);
      return;
    }

    console.log('[HowlerBackend] Tab visible, restoring audio...');
    this.tabHidden = false;
    Howler.mute(false);
    this.applyMusicMuteState();

    if (this.currentMusicName && !this.musicMuted) {
      this.ensureMusicIsPlaying();
    }
  };

  private soundMap: Record<string, string> = {
    bet: 'sfx/bet.mp3',
    cashout: 'sfx/cashout.mp3',
    start: 'sfx/start.mp3',
    crash: 'sfx/crash.mp3',
  };

  constructor(opts: HowlerBackendOptions) {
    this.slug = opts.slug;
    this.assets = opts.assetManager;
    this.duckMusicOnSfx = opts.duckMusicOnSfx ?? false;
    this.musicDuckRatio = opts.musicDuckRatio ?? 0.25;
    this.soundVolumes = opts.soundVolumes;
    if (opts.soundMap) {
      this.soundMap = { ...this.soundMap, ...opts.soundMap };
    }

    // Load persisted mute states from localStorage
    this.loadPersistedMuteStates();

    Howler.volume(this.volume);

    this.soundsReady = this.preloadSounds();
    void this.soundsReady.then(() => {
      if (
        this.pendingMusicRequest &&
        this.userHasInteracted &&
        !this.musicMuted
      ) {
        const track = this.pendingMusicRequest;
        this.pendingMusicRequest = null;
        this.playMusicAfterPreload(track);
      }
    });
    this.setupVisibilityListener();
    this.setupUserGestureAudioUnlock();

    this.initializeAudioOnLoad();
    registerActiveAudio(this);
  }

  // Load persisted mute states from localStorage
  private loadPersistedMuteStates(): void {
    const snapshot = sanitizeAudioStorage();
    this.sfxMuted = snapshot.sfxMuted;
    this.musicMuted = snapshot.musicMuted;

    console.log('[sound-debug] HowlerService: Loaded mute state snapshot', {
      sfxMuted: this.sfxMuted,
      musicMuted: this.musicMuted,
      wasReset: snapshot.wasReset,
    });
    console.log(
      '[audio-check] HowlerService.loadPersisted -> applied snapshot',
      {
        sfxMuted: this.sfxMuted,
        musicMuted: this.musicMuted,
      }
    );

    // Apply mute state to any currently playing music
    // This ensures that if music is already playing, it respects the loaded mute state
    this.applyMusicMuteState();
  }

  // Apply the current mute state to any playing music
  private applyMusicMuteState(): void {
    if (this.currentMusic && this.currentMusic.id !== undefined) {
      console.log(
        '[sound-debug] HowlerService: Applying mute state to current music',
        {
          musicMuted: this.musicMuted,
          musicId: this.currentMusic.id,
        }
      );
      // CRITICAL: Always apply mute state to both instance-level AND sound ID-level
      // Apply at instance level first
      this.currentMusic.howl.mute(this.musicMuted);
      // Then apply to the specific sound ID to ensure it takes effect
      this.currentMusic.howl.mute(this.musicMuted, this.currentMusic.id);
    }
  }

  // --- NEW: Initialize audio based on game phase ---
  private initializeAudioOnLoad(): void {
    // Assuming there's an event or method to check game phase
    GlobalBus.on('game:phase:loaded', (phase: string) => {
      console.log(`[HowlerBackend] Game phase on load: ${phase}`);
      if (phase === 'FLYING' && !this.musicMuted) {
        // Adjusted to match actual phase name 'FLYING'
        // Play background music only if in FLYING phase
        const bgMusic = Object.keys(this.soundMap).find(key =>
          key.includes('bgMusic')
        );
        if (bgMusic) {
          this.playMusic(bgMusic);
        }
      }
    });

    // Trigger phase check on initialization
    GlobalBus.emit('game:request:phase');
  }

  private resolveSoundSources(path: string, isMusic = false): string[] {
    const mp3Url = `${this.assets.baseUrl}/games/${this.slug}/${path}`;
    if (path.includes('/MP3/')) {
      const oggPath = path.replace('/MP3/', '/OGG/').replace(/\.mp3$/i, '.ogg');
      const oggUrl = `${this.assets.baseUrl}/games/${this.slug}/${oggPath}`;
      // HTML5 music streams more reliably from MP3 on mobile browsers.
      return isMusic ? [mp3Url, oggUrl] : [oggUrl, mp3Url];
    }
    return [mp3Url];
  }

  private isMusicAudible(): boolean {
    if (
      !this.currentMusic ||
      this.currentMusic.id === undefined ||
      this.musicMuted
    ) {
      return false;
    }
    return this.currentMusic.howl.playing(this.currentMusic.id);
  }

  private async resumeAudioContext(): Promise<void> {
    try {
      const ctx = Howler.ctx;
      if (ctx && ctx.state === 'suspended') {
        await ctx.resume();
        console.log('[HowlerBackend] Audio context resumed');
      }
    } catch (error) {
      console.warn('[HowlerBackend] Failed to resume audio context', error);
    }
  }

  /**
   * Unlock WebAudio and start queued BGM only after a real user gesture.
   * Required by browser autoplay policy on desktop and mobile.
   */
  private setupUserGestureAudioUnlock(): void {
    if (this.musicAutoplayListener) {
      return;
    }

    this.musicAutoplayListener = () => {
      this.noteUserGesture();
      void this.resumeAudioContext().then(() => {
        if (this.musicMuted || this.tabHidden) {
          return;
        }
        const track = this.pendingMusicRequest ?? this.currentMusicName;
        if (!track || this.isMusicAudible()) {
          return;
        }
        this.playMusicAfterPreload(track);
      });
    };

    document.addEventListener('pointerdown', this.musicAutoplayListener, {
      capture: true,
    });
    document.addEventListener('click', this.musicAutoplayListener, {
      capture: true,
    });
    document.addEventListener('touchstart', this.musicAutoplayListener, {
      capture: true,
    });
    document.addEventListener('keydown', this.musicAutoplayListener, {
      capture: true,
    });
  }

  private isMusicSound(name: string, path: string): boolean {
    return (
      (name.includes('bgMusic') ||
        name.includes('Bg') ||
        name === 'dogLoading') &&
      name !== 'lightingBg' &&
      !path.includes('wind_loop')
    );
  }

  /** Resolve Howler volume for a sound; game config overrides built-in defaults. */
  private resolveIndividualVolume(
    name: string,
    path: string,
    isMusic: boolean
  ): number {
    const configured = this.soundVolumes?.[name];
    if (configured !== undefined) {
      return configured;
    }

    let individualVolume = 0.35;
    if (name === 'start') {
      individualVolume = 2.0;
    } else if (name === 'crash' || name === 'blast' || name === 'dogCrash') {
      individualVolume = 1.0;
    } else if (name === 'bet') {
      individualVolume = 1.0;
    } else if (name === 'dogJump') {
      individualVolume = 0.15;
    } else if (name === 'lightingBg') {
      individualVolume = 0.8;
    } else if (name === 'multiplierInc') {
      individualVolume = 1.5;
    } else if (name === 'thunderCashout') {
      individualVolume = 1.5;
    } else if (name === 'windLoop') {
      individualVolume = 0.45;
    } else if (name === 'dogLoading') {
      individualVolume = 0.6;
    } else if (isMusic) {
      individualVolume = 0.2;
    }

    return individualVolume;
  }

  private isMusicTrackName(name: string): boolean {
    return (
      name.includes('bgMusic') ||
      name.includes('music') ||
      name === 'dogLoading'
    );
  }

  private isResumableSfx(name: string): boolean {
    return name === 'start';
  }

  private applyResumableSfxMute(muted: boolean): void {
    for (const [name, howl] of this.sounds.entries()) {
      if (this.isResumableSfx(name)) {
        howl.mute(muted);
      }
    }
  }

  /** Immediately silence every non-music SFX instance (one-shots and loops). */
  private stopAllPlayingSfx(): void {
    this.sfxCallbackEpoch += 1;
    for (const [name, howl] of this.sounds.entries()) {
      if (this.isMusicTrackName(name)) {
        continue;
      }

      if (this.isResumableSfx(name)) {
        howl.mute(true);
        continue;
      }

      const loopId = this.loopSounds.get(name);
      if (loopId !== undefined) {
        howl.stop(loopId);
        howl.loop(false, loopId);
      } else {
        howl.stop();
      }
      howl.loop(false);
    }

    this.loopSounds.clear();
    this.activeSfxCount = 0;

    if (
      this.duckMusicOnSfx &&
      this.currentMusic?.id !== undefined &&
      !this.musicMuted
    ) {
      this.currentMusic.howl.volume(
        this.musicNormalVolume,
        this.currentMusic.id
      );
    }
  }

  private duckMusicIfNeeded(): void {
    if (!this.duckMusicOnSfx || !this.currentMusic?.id || this.musicMuted) {
      return;
    }
    const duckedVolume = this.musicNormalVolume * this.musicDuckRatio;
    this.currentMusic.howl.volume(duckedVolume, this.currentMusic.id);
  }

  private releaseSfxDuck(): void {
    if (!this.duckMusicOnSfx) {
      return;
    }
    this.activeSfxCount = Math.max(0, this.activeSfxCount - 1);
    if (
      this.activeSfxCount === 0 &&
      this.currentMusic?.id !== undefined &&
      !this.musicMuted
    ) {
      this.currentMusic.howl.volume(
        this.musicNormalVolume,
        this.currentMusic.id
      );
    }
  }

  private registerSfxDuck(
    sound: Howl,
    soundId: number | undefined
  ): () => void {
    if (!this.duckMusicOnSfx || soundId === undefined) {
      return () => {};
    }

    let released = false;
    const release = () => {
      if (released) {
        return;
      }
      released = true;
      this.releaseSfxDuck();
    };

    if (this.activeSfxCount === 0) {
      this.duckMusicIfNeeded();
    }
    this.activeSfxCount++;
    sound.once('end', release, soundId);
    return release;
  }

  private async preloadSounds(): Promise<void> {
    console.log('[HowlerBackend] Preloading sounds...');
    const soundPromises = Object.entries(this.soundMap).map(
      async ([name, path]) => {
        try {
          const isMusic = this.isMusicSound(name, path);
          const sources = this.resolveSoundSources(path, isMusic);

          const individualVolume = this.resolveIndividualVolume(
            name,
            path,
            isMusic
          );
          if (name === 'bgMusic') {
            this.musicNormalVolume = individualVolume;
          }

          const howl = new Howl({
            src: sources,
            preload: true,
            volume: individualVolume,
            pool: isMusic ? 1 : 3,
            loop: isMusic,
            html5: false, //isMusic, // Use HTML5 Audio for music for better mobile support
            onload: () =>
              console.log(
                `[HowlerBackend] Loaded sound: ${name} with volume ${individualVolume}`
              ),
            onloaderror: (_, error) =>
              console.error(
                `[HowlerBackend] Failed to load sound ${name}:`,
                error
              ),
            onend: () => {
              if (isMusic) {
                console.warn(
                  `[HowlerBackend] Music "${name}" ended unexpectedly`
                );
              }
            },
          });
          this.sounds.set(name, howl);
        } catch (error) {
          console.error(
            `[HowlerBackend] Error creating Howl for ${name}:`,
            error
          );
        }
      }
    );

    await Promise.all(soundPromises);
    console.log(
      `[HowlerBackend] Preloading complete. ${this.sounds.size} sounds loaded.`
    );

    // Apply mute state to all music instances after preloading
    // This ensures music instances are muted before they can be played
    for (const [name, howl] of this.sounds.entries()) {
      if (
        name.includes('bgMusic') ||
        name.includes('music') ||
        name === 'dogLoading'
      ) {
        howl.mute(this.musicMuted);
        console.log(
          `[sound-debug] HowlerService: Applied mute state to preloaded music "${name}"`,
          {
            musicMuted: this.musicMuted,
            instanceMute: howl.mute(),
          }
        );
      }
    }
  }

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  noteUserGesture(): void {
    if (!this.userHasInteracted) {
      this.userHasInteracted = true;
      console.log(
        '[HowlerBackend] User interaction detected, SFX can now play'
      );
      // Mirror music unlock: once gestured, let the game resume any SFX that
      // were deferred while Sound preference stayed ON across reload.
      if (!this.sfxMuted && !this.tabHidden) {
        GlobalBus.emit('audio:state:changed', { isMuted: false });
      }
    }
  }

  private startMusicHealthCheck(): void {
    this.stopMusicHealthCheck();
    this.musicHealthCheckInterval = setInterval(() => {
      this.ensureMusicIsPlaying();
    }, 5000);
    console.log(
      '[HowlerBackend] Music health check started (checking every 5 seconds)'
    );
  }

  private stopMusicHealthCheck(): void {
    if (this.musicHealthCheckInterval) {
      clearInterval(this.musicHealthCheckInterval);
      this.musicHealthCheckInterval = null;
      console.log('[HowlerBackend] Music health check stopped');
    }
  }

  private ensureMusicIsPlaying(): void {
    if (
      !this.userHasInteracted ||
      !this.currentMusicName ||
      this.musicMuted ||
      this.tabHidden
    ) {
      return;
    }

    if (!this.currentMusic || !this.isMusicAudible()) {
      console.warn('[HowlerBackend] Music not audible, retrying autoplay...');
      void this.resumeAudioContext().then(() => {
        const track = this.currentMusicName;
        if (
          !track ||
          this.musicMuted ||
          this.isMusicAudible() ||
          this.musicPlayPending
        ) {
          return;
        }
        const music = this.sounds.get(track);
        if (music) {
          // Stop the stale sound first so Howler marks it _ended and can
          // reuse its slot — otherwise this retry path (pool: 1 for music)
          // creates a brand-new Sound each time and permanently consumes
          // one more slot from Howler's global HTML5 audio pool.
          this.stopMusic();
          // stopMusic() nulls currentMusicName; playMusicInternal doesn't
          // restore it on the success path (its caller normally does).
          this.currentMusicName = track;
          this.playMusicInternal(music, track);
        }
      });
      return;
    }

    const now = Date.now();
    if (this.musicDuration > 0 && this.musicStartTime > 0) {
      const timeSinceStart = now - this.musicStartTime;
      const expectedLoops = Math.floor(
        timeSinceStart / (this.musicDuration * 1000)
      );
      const timeSinceLastLoop = now - this.lastLoopTime;

      if (
        expectedLoops > 0 &&
        timeSinceLastLoop > this.musicDuration * 1000 * 1.5
      ) {
        console.warn(
          `[HowlerBackend] Music should have looped by now. ` +
            `Duration: ${this.musicDuration}s, Time since last loop: ${(timeSinceLastLoop / 1000).toFixed(1)}s. ` +
            `Restarting music...`
        );
        this.playMusic(this.currentMusicName);
        return;
      }
    }

    const { howl, id } = this.currentMusic;
    if (id !== undefined && !howl.playing(id)) {
      console.warn('[HowlerBackend] Music not playing, restarting...');
      this.playMusic(this.currentMusicName);
      return;
    }

    if (id !== undefined && !howl.loop(id)) {
      console.warn('[HowlerBackend] Music loop disabled, re-enabling...');
      howl.loop(true, id);
    }

    if (id !== undefined && howl.mute() !== this.musicMuted) {
      console.warn('[HowlerBackend] Music mute state incorrect, fixing...', {
        currentMuteState: howl.mute(),
        expectedMuteState: this.musicMuted,
        musicId: id,
      });
      // Apply mute state at both instance level and sound ID level for reliability
      howl.mute(this.musicMuted);
      howl.mute(this.musicMuted, id);
    }
  }

  getSfxDuration(name: string): number | undefined {
    const sound = this.sounds.get(name);
    if (!sound) {
      return undefined;
    }
    const duration = sound.duration();
    return duration > 0 ? duration : undefined;
  }

  isSfxPlaying(name: string): boolean {
    const sound = this.sounds.get(name);
    return Boolean(sound?.playing());
  }

  private canPlaySfx(_name: string): boolean {
    if (this.sfxMuted || this.tabHidden) {
      return false;
    }
    // Same gesture gate as music: never autoplay SFX before a real click/tap.
    if (!this.userHasInteracted) {
      return false;
    }

    return true;
  }

  play(name: string): void {
    if (!this.canPlaySfx(name)) {
      return;
    }

    const sound = this.sounds.get(name);
    if (sound) {
      sound.mute(false);
      // Restart from a clean instance so rapid mute/unmute cannot leave a
      // pooled sprite playing or locked, which skipped takeoff after toggles.
      if (sound.playing() && !this.isResumableSfx(name)) {
        sound.stop();
      } else if (sound.playing() && this.isResumableSfx(name)) {
        return;
      }
      console.log(`[HowlerBackend] Playing SFX "${name}"`);
      const soundId = sound.play();
      const releaseDuck = this.registerSfxDuck(sound, soundId);

      // For crash / blast sounds, stop after the initial impact
      if (
        name === 'crash' ||
        name === 'blast' ||
        name === 'dogCrash' ||
        name.toLowerCase().includes('crash')
      ) {
        setTimeout(() => {
          if (soundId !== undefined) {
            sound.stop(soundId);
            releaseDuck();
            console.log(
              `[HowlerBackend] Stopped crash sound "${name}" at natural pause`
            );
          }
        }, 1000);
      }

      // For lightning sounds, stop after the lightning animation duration (350ms max)
      if (name === 'lightingBg') {
        setTimeout(() => {
          if (soundId !== undefined) {
            sound.stop(soundId);
            console.log(
              `[HowlerBackend] Stopped lightning sound "${name}" after animation duration`
            );
          }
        }, 2500); // Match the maximum lightning bolt duration
      }
    } else {
      console.warn(`[HowlerBackend] No sound found for "${name}"`);
    }
  }

  stop(name: string): void {
    const sound = this.sounds.get(name);
    if (!sound) {
      return;
    }
    sound.stop();
    console.log(`[HowlerBackend] Stopped SFX "${name}"`);
  }

  playAt(name: string, seekSeconds: number): void {
    if (!this.canPlaySfx(name)) {
      return;
    }

    const sound = this.sounds.get(name);
    if (!sound) {
      console.warn(`[HowlerBackend] No sound found for "${name}"`);
      return;
    }

    const clampedSeek = Math.max(0, seekSeconds);
    const duration = sound.duration();
    if (duration > 0 && clampedSeek >= duration) {
      return;
    }

    // Already running (often muted in place) — un-mute and keep the same instance.
    if (sound.playing()) {
      sound.mute(false);
      if (clampedSeek > 0) {
        sound.seek(clampedSeek);
      }
      return;
    }

    sound.mute(false);
    console.log(
      `[HowlerBackend] Playing SFX "${name}" from ${clampedSeek.toFixed(2)}s`
    );
    const soundId = sound.play();
    if (soundId === undefined) {
      return;
    }
    const applySeek = () => {
      if (this.sfxMuted || soundId === undefined) {
        return;
      }
      if (clampedSeek > 0) {
        sound.seek(clampedSeek, soundId);
      }
    };
    sound.once('play', applySeek, soundId);
    this.registerSfxDuck(sound, soundId);
  }

  playWithCallback(name: string, onEnd: () => void): void {
    this.sfxCallbackEpoch += 1;
    const epoch = this.sfxCallbackEpoch;

    if (!this.canPlaySfx(name)) {
      onEnd();
      return;
    }

    const sound = this.sounds.get(name);
    if (sound) {
      if (sound.playing()) {
        sound.stop();
      }
      const soundId = sound.play();
      const releaseDuck = this.registerSfxDuck(sound, soundId);

      let ended = false;
      const finish = (invokeCallback: boolean) => {
        if (ended) {
          return;
        }
        ended = true;
        if (epoch !== this.sfxCallbackEpoch) {
          return;
        }
        if (!invokeCallback || this.sfxMuted || this.tabHidden) {
          return;
        }
        onEnd();
      };

      // Listen for the end event on this specific sound instance
      sound.once(
        'end',
        () => {
          finish(true);
        },
        soundId
      );

      // Crash SFX: wait for natural clip end (no 1s cut-off) so completion callbacks fire.
      const isCrashLike =
        name === 'crash' ||
        name === 'blast' ||
        name === 'dogCrash' ||
        name.toLowerCase().includes('crash');
      const durationSec = this.getSfxDuration(name);
      if (isCrashLike) {
        if (durationSec !== undefined) {
          setTimeout(
            () => {
              if (
                !ended &&
                soundId !== undefined &&
                epoch === this.sfxCallbackEpoch
              ) {
                sound.stop(soundId);
                releaseDuck();
                finish(true);
              }
            },
            Math.ceil(durationSec * 1000) + 50
          );
        }
      } else if (durationSec !== undefined) {
        setTimeout(
          () => {
            if (soundId !== undefined && !sound.playing(soundId)) {
              finish(false);
              return;
            }
            finish(true);
          },
          Math.ceil(durationSec * 1000) + 50
        );
      }

      // Handle lightning sounds with auto-stop
      if (name === 'lightingBg') {
        setTimeout(() => {
          if (soundId !== undefined) {
            sound.stop(soundId);
          }
        }, 2500);
      }
    } else {
      // Still call the callback even if sound not found
      onEnd();
    }
  }

  playLoop(name: string): void {
    if (!this.canPlaySfx(name)) {
      return;
    }

    const sound = this.sounds.get(name);
    if (!sound) {
      console.warn(`[HowlerBackend] No loop sound found for "${name}"`);
      return;
    }

    this.stopLoop(name);

    sound.loop(true);
    const soundId = sound.play();
    if (soundId === undefined) {
      return;
    }

    sound.loop(true, soundId);
    this.loopSounds.set(name, soundId);
    console.log(`[HowlerBackend] Playing loop "${name}"`);
  }

  stopLoop(name: string): void {
    const sound = this.sounds.get(name);
    if (!sound) {
      return;
    }

    const soundId = this.loopSounds.get(name);
    if (soundId !== undefined) {
      sound.stop(soundId);
      this.loopSounds.delete(name);
    }

    sound.loop(false);
    console.log(`[HowlerBackend] Stopped loop "${name}"`);
  }

  playMusic(name: string): void {
    this.pendingMusicRequest = name;
    void this.soundsReady.then(() => {
      if (this.pendingMusicRequest !== name) {
        return;
      }
      this.pendingMusicRequest = null;
      this.playMusicAfterPreload(name);
    });
  }

  private playMusicAfterPreload(name: string): void {
    this.currentMusicName = name;
    this.pendingMusicRequest = name;

    if (this.musicMuted) {
      console.log(
        `[HowlerBackend] Music "${name}" deferred — music toggle is off`
      );
      return;
    }

    if (!this.userHasInteracted) {
      console.log(
        `[HowlerBackend] Music "${name}" deferred — waiting for user gesture`
      );
      return;
    }

    void this.resumeAudioContext().then(() => {
      if (this.musicMuted || !this.userHasInteracted) {
        return;
      }

      if (this.isMusicAudible() && this.currentMusicName === name) {
        return;
      }

      if (this.musicPlayPending && this.currentMusicName === name) {
        // A play() for this same track hasn't settled yet — restarting now
        // would race Howler's stop()/_playLock queueing (see musicPlayPending
        // doc comment) and leak another HTML5 pool slot. Let it settle.
        console.log(
          `[HowlerBackend] Music "${name}" restart already in flight, skipping`
        );
        return;
      }

      this.stopMusic();
      this.currentMusicName = name;
      this.musicStartTime = Date.now();
      this.lastLoopTime = Date.now();

      const music = this.sounds.get(name);
      if (music) {
        if (music.state() !== 'loaded') {
          music.once('load', () => {
            this.playMusicInternal(music, name);
          });
          music.once('loaderror', () => {
            console.error(
              `[HowlerBackend] Failed to load music "${name}", will retry automatically`
            );
            this.currentMusicName = name;
            this.startMusicHealthCheck();
          });
          return;
        }

        this.playMusicInternal(music, name);
        return;
      }

      console.warn(`[HowlerBackend] No music track found for "${name}"`, {
        requestedName: name,
        availableSounds: Array.from(this.sounds.keys()),
        soundMapKeys: Object.keys(this.soundMap),
      });
      this.currentMusicName = null;
    });
  }

  private playMusicInternal(music: Howl, name: string): void {
    // CRITICAL: Set mute state BEFORE playing to prevent any sound from playing
    // Apply sound ID-level mute will be set after play() returns the ID
    // Set instance-level mute first
    music.mute(this.musicMuted);

    this.musicDuration = music.duration();
    console.log(
      `[HowlerBackend] Music duration: ${this.musicDuration.toFixed(2)}s`
    );

    const musicId = music.play();

    if (musicId === undefined) {
      console.warn(
        `[HowlerBackend] Music "${name}" blocked by browser autoplay policy — will retry`
      );
      this.currentMusicName = name;
      this.startMusicHealthCheck();
      return;
    }

    // Howler resolves/rejects the underlying play() promise asynchronously —
    // musicId above is always defined synchronously regardless of whether
    // playback actually starts, so autoplay-block only surfaces via this
    // 'playerror' event, not the check above.
    this.musicPlayPending = true;
    music.once(
      'playerror',
      () => {
        this.musicPlayPending = false;
        console.warn(
          `[HowlerBackend] Music "${name}" blocked by browser autoplay policy — will retry`
        );
      },
      musicId
    );

    // CRITICAL: Apply mute state to sound ID FIRST (most specific), then instance-level
    // This ensures the mute state is properly synchronized
    music.mute(this.musicMuted, musicId);
    music.mute(this.musicMuted);

    // Force re-apply to ensure it sticks
    music.mute(this.musicMuted, musicId);

    music.loop(true, musicId);
    music.volume(this.musicNormalVolume, musicId);
    this.currentMusic = { howl: music, id: musicId };

    // Verify mute state was applied correctly
    const instanceMuteState = music.mute();
    console.log('[sound-debug] HowlerService: Music started with mute state', {
      musicMuted: this.musicMuted,
      musicId: musicId,
      instanceMuteState: instanceMuteState,
      expectedMuteState: this.musicMuted,
      muteStateMatches: instanceMuteState === this.musicMuted,
    });

    // If mute state doesn't match, log a warning
    if (instanceMuteState !== this.musicMuted) {
      console.warn('[sound-debug] HowlerService: Mute state mismatch!', {
        expected: this.musicMuted,
        actual: instanceMuteState,
        musicId: musicId,
      });
      // Try to fix it
      music.mute(this.musicMuted);
      music.mute(this.musicMuted, musicId);
    }

    music.on(
      'end',
      () => {
        console.log(`[HowlerBackend] Music "${name}" ended (natural loop)`);
        this.lastLoopTime = Date.now();
        if (this.currentMusicName === name && !this.musicMuted) {
          if (!music.loop(musicId)) {
            console.warn(
              `[HowlerBackend] Loop was disabled, restarting music...`
            );
            setTimeout(() => {
              if (this.currentMusicName === name) {
                this.playMusic(name);
              }
            }, 100);
          }
        }
      },
      musicId
    );

    music.on(
      'stop',
      () => {
        if (
          this.currentMusicName === name &&
          this.currentMusic?.id === musicId
        ) {
          console.warn(`[HowlerBackend] Music "${name}" stopped unexpectedly`);
        }
      },
      musicId
    );

    music.on(
      'play',
      () => {
        this.musicPlayPending = false;
        const now = Date.now();
        if (
          this.lastLoopTime > 0 &&
          now - this.lastLoopTime > this.musicDuration * 1000 * 0.9
        ) {
          console.log(`[HowlerBackend] Loop detected via play event`);
          this.lastLoopTime = now;
        }
      },
      musicId
    );

    this.startMusicHealthCheck();
  }

  stopMusic(): void {
    this.stopMusicHealthCheck();
    this.currentMusicName = null;
    this.musicStartTime = 0;
    this.musicDuration = 0;
    this.lastLoopTime = 0;

    this.musicPlayPending = false;

    if (this.currentMusic?.id !== undefined) {
      console.log('[HowlerBackend] Stopping music.');
      try {
        this.currentMusic.howl.stop(this.currentMusic.id);
        this.currentMusic.howl.off('end', undefined, this.currentMusic.id);
        this.currentMusic.howl.off('stop', undefined, this.currentMusic.id);
        this.currentMusic.howl.off('play', undefined, this.currentMusic.id);
        this.currentMusic.howl.off(
          'playerror',
          undefined,
          this.currentMusic.id
        );
      } catch (err) {
        console.warn('[HowlerBackend] Error stopping music:', err);
      }
      this.currentMusic = null;
    }
  }

  setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
    Howler.volume(this.volume);
    console.log(`[HowlerBackend] Global volume set to ${this.volume}`);
  }

  getVolume(): number {
    return this.volume;
  }

  muteSfx(): void {
    this.sfxMuted = true;
    this.applyResumableSfxMute(true);
    this.stopAllPlayingSfx();
    console.log('[HowlerBackend] SFX Muted');
  }

  unmuteSfx(): void {
    this.sfxMuted = false;
    this.applyResumableSfxMute(false);
    console.log('[HowlerBackend] SFX Unmuted');
  }

  isSfxMuted(): boolean {
    return this.sfxMuted;
  }

  muteMusic(): void {
    console.log('[sound-debug] HowlerService.muteMusic: Called', {
      currentMusic: this.currentMusic,
      currentMusicId: this.currentMusic?.id,
      currentMusicName: this.currentMusicName,
      musicMutedBefore: this.musicMuted,
    });
    this.musicMuted = true;

    // Stop the active music instance immediately so audio cannot keep playing.
    if (this.currentMusic) {
      if (this.currentMusic.id !== undefined) {
        this.currentMusic.howl.stop(this.currentMusic.id);
      }
      this.currentMusic.howl.stop();
      this.currentMusic = null;
    }

    // Mute all preloaded music instances as a fallback.
    for (const [name, howl] of this.sounds.entries()) {
      if (this.isMusicTrackName(name)) {
        howl.mute(true);
      }
    }

    console.log('[HowlerBackend] Music Muted');
  }

  unmuteMusic(): void {
    console.log('[sound-debug] HowlerService.unmuteMusic: Called', {
      currentMusic: this.currentMusic,
      currentMusicId: this.currentMusic?.id,
      currentMusicName: this.currentMusicName,
      musicMutedBefore: this.musicMuted,
    });
    this.musicMuted = false;

    // Try to unmute the current music instance
    if (this.currentMusic) {
      const isPlaying =
        this.currentMusic.id !== undefined &&
        this.currentMusic.howl.playing(this.currentMusic.id);
      console.log(
        '[sound-debug] HowlerService.unmuteMusic: Applying unmute to Howl instance',
        {
          musicId: this.currentMusic.id,
          howlState: this.currentMusic.howl.state(),
          isPlaying,
        }
      );

      // CRITICAL: Always apply unmute to both instance-level AND sound ID-level
      // The sound ID-level mute can override instance-level mute, so both must be set
      // Apply sound ID-level unmute FIRST, then instance-level, to ensure it takes effect
      if (this.currentMusic.id !== undefined) {
        // Set sound ID-level unmute first (this is the most specific)
        this.currentMusic.howl.mute(false, this.currentMusic.id);
      }
      // Then set instance-level unmute (affects all sounds from this instance)
      this.currentMusic.howl.mute(false);

      // Force re-apply to ensure it sticks (Howler.js sometimes needs this)
      if (this.currentMusic.id !== undefined) {
        this.currentMusic.howl.mute(false, this.currentMusic.id);
      }

      // Use requestAnimationFrame to ensure unmute is applied after Howler.js processes
      requestAnimationFrame(() => {
        if (this.currentMusic && this.currentMusic.id !== undefined) {
          // Double-check and re-apply if needed
          this.currentMusic.howl.mute(false);
          this.currentMusic.howl.mute(false, this.currentMusic.id);

          // If music isn't playing after unmuting, restart it
          if (
            !this.currentMusic.howl.playing(this.currentMusic.id) &&
            this.currentMusicName
          ) {
            console.log(
              '[sound-debug] HowlerService.unmuteMusic: Music not playing, restarting...'
            );
            this.playMusic(this.currentMusicName);
          }
        }
      });

      console.log(
        '[sound-debug] HowlerService.unmuteMusic: Unmute applied, verifying...',
        {
          muteState: this.currentMusic.howl.mute(),
          musicId: this.currentMusic.id,
        }
      );
    } else {
      console.warn(
        '[sound-debug] HowlerService.unmuteMusic: No currentMusic, trying to unmute all music sounds',
        {
          currentMusicName: this.currentMusicName,
        }
      );

      // Fallback: unmute all music sounds if currentMusic is not set
      for (const [name, howl] of this.sounds.entries()) {
        if (
          name.includes('bgMusic') ||
          name.includes('music') ||
          name === 'dogLoading'
        ) {
          console.log(
            `[sound-debug] HowlerService.unmuteMusic: Unmuting all instances of ${name}`
          );
          howl.mute(false);
        }
      }

      // CRITICAL FIX: If no music is currently playing, start playing bgMusic
      // This handles the case where music was muted on reload and never started
      const bgMusicName = Object.keys(this.soundMap).find(key =>
        key.includes('bgMusic')
      );
      if (bgMusicName && !this.currentMusicName) {
        console.log(
          `[sound-debug] HowlerService.unmuteMusic: Starting bgMusic since no music was playing`
        );
        this.playMusic(bgMusicName);
      } else if (this.currentMusicName && !this.currentMusic) {
        // If we have a music name but no current instance, restart it
        console.log(
          `[sound-debug] HowlerService.unmuteMusic: Restarting music: ${this.currentMusicName}`
        );
        this.playMusic(this.currentMusicName);
      }
    }
    console.log('[HowlerBackend] Music Unmuted');
  }

  isMusicMuted(): boolean {
    return this.musicMuted;
  }

  isMusicPlaying(): boolean {
    return this.isMusicAudible();
  }

  debugAudioStatus(): void {
    console.log('[HowlerBackend] Status:', {
      slug: this.slug,
      baseUrl: this.assets.baseUrl,
      volume: this.volume,
      sfxMuted: this.sfxMuted,
      musicMuted: this.musicMuted,
      soundsLoaded: this.sounds.size,
      currentMusic: this.currentMusic?.howl.state(),
      soundMap: this.soundMap,
    });
  }

  dispose(): void {
    unregisterActiveAudio(this);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.musicAutoplayListener) {
      document.removeEventListener(
        'pointerdown',
        this.musicAutoplayListener,
        true
      );
      document.removeEventListener('click', this.musicAutoplayListener, true);
      document.removeEventListener(
        'touchstart',
        this.musicAutoplayListener,
        true
      );
      document.removeEventListener('keydown', this.musicAutoplayListener, true);
      this.musicAutoplayListener = null;
    }
    this.stopMusicHealthCheck();
    this.stopMusic();
    for (const name of Array.from(this.loopSounds.keys())) {
      this.stopLoop(name);
    }

    // Release decoded WebAudio buffers / HTML5 audio nodes — otherwise every
    // Howl created in preloadSounds() stays registered in Howler._howls
    // forever, leaking a full sound set on every game switch.
    for (const howl of this.sounds.values()) {
      howl.unload();
    }
    this.sounds.clear();
  }
}
